import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Modal, Table, Tooltip } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import moment from "moment";

import { useLeadFormActions } from "./hooks/useLeadFormActions";
import { PAGE_LENGTH } from "../../config";
import { configType } from "../../constant/WebToLead";
import ActionMenu from "./ActionMenu";
import {
  emptyGeneratedHtml,
  generateHtml,
} from "../../actionCreator/webToLeadActionCreater";
import Status from "./Status";
import htmlIcon from "../../assets/images/webToLead/html.svg";
import linkIcon from "../../assets/images/webToLead/link.svg";
import { getSubscriptionUserInfo } from "../../utils/utils";
import Preview from "./Preview";
import Share from "./Share";
import CustomPagination from "../../common/CustomPagination";
import NoTableData from "../NoTableData";

const FormsList = ({ onSubscription }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { leadForms, loading, getLeadFormsList } = useLeadFormActions();
  const [page, setPage] = useState(1);
  const [showPreview, setShowPreview] = useState(null);
  const generatedHtml = useSelector(
    (state) => state.WebToLeadReducer.generatedHtml,
  );
  const previewLoading = useSelector((state) => state.WebToLeadReducer.loading);
  const subscription = getSubscriptionUserInfo();
  const currentDate = moment();
  const validToDate = moment(subscription?.validTo);
  const isExpired = validToDate.isBefore(currentDate);
  const dayDiff = currentDate.diff(validToDate, "days");

  const onPageChange = (page, pageSize) => {
    setPage(page);
  };

  const handleCancel = () => {
    setShowPreview(null);
    dispatch(emptyGeneratedHtml());
  };

  useEffect(() => {
    getLeadFormsList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const columns = [
    {
      title: <></>,
      dataIndex: "active",
      width: 70,
      render: (record, row) => {
        return (
          <div className="table-forms">
            {row.configType === configType.EMBEDDED_HTML && (
              <Tooltip title="Preview Form">
                <div
                  className={`preview ${showPreview === row?.id && "preview-active"}`}
                  onClick={() => {
                    dispatch(
                      generateHtml(
                        row?.id,
                        row.configType === configType.EMBEDDED_SCRIPT,
                      ),
                    );
                    setShowPreview(row?.id);
                  }}
                >
                  <EyeOutlined />
                </div>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: <span className="tw-text-sm ">Forms</span>,
      dataIndex: "formName",
      render: (record, row) => {
        return (
          <div className="d-flex align-items-center">
            <div
              className="tw-text-theme tw-text-sm tw-cursor-pointer"
              onClick={() => {
                if (isExpired && dayDiff > 7) {
                  onSubscription();
                } else {
                  navigate(`/settings/web-to-lead/${row.id}`);
                }
              }}
            >
              {record}
            </div>
          </div>
        );
      },
    },
    {
      title: <span className="tw-text-sm ">Form type</span>,
      dataIndex: "configType",
      render: (record, row) => {
        return (
          <>
            {record !== configType.SHARED_URL ? (
              <div className="d-flex align-items-center" style={{ gap: 5 }}>
                <img src={htmlIcon} alt="" width={16} height={16} />{" "}
                <span className="html-badge">
                  {record === configType.EMBEDDED_SCRIPT ? "Script" : "HTML"}
                </span>
                <div className="mx-2">
                  <Share formData={row} isCopy />
                </div>
              </div>
            ) : (
              <div className="d-flex align-items-center" style={{ gap: 5 }}>
                <img src={linkIcon} alt="" width={16} height={16} />
                <span className="link-badge">Link</span>
              </div>
            )}
          </>
        );
      },
    },
    {
      title: <span className="tw-text-sm">Created Date</span>,
      dataIndex: "createdDate",
      render: (record, row) => {
        return (
          <div className="tw-text-sm">
            {moment(record).format("MMM DD, YYYY")}
          </div>
        );
      },
    },
    {
      title: <span className="tw-text-sm ">Status</span>,
      dataIndex: "active",
      render: (record, row) => {
        return <Status record={record} row={row} page={page} />;
      },
    },
    {
      title: <></>,
      dataIndex: "active",
      render: (record, row) => {
        return (
          <ActionMenu
            row={row}
            page={page}
            setPage={setPage}
            isExpired={isExpired && dayDiff > 7}
            onSubscription={onSubscription}
          />
        );
      },
    },
  ];

  const tableProps =
    isExpired && dayDiff > 7
      ? {
          locale: {
            emptyText: (
              <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-5">
                <NoTableData />
                <div className="text-error">
                  Your subscription has expired. Please renew your subscription
                  to continue using the service.
                </div>
              </div>
            ),
          },
        }
      : {};

  return (
    <div className="mt20">
      <div className="tw-custom-table-container">
        <Table
          columns={columns}
          loading={loading}
          dataSource={leadForms?.content}
          size="middle"
          className="tw-custom-table tw-web-to-lead-table tw-table-max-content"
          pagination={false}
          {...tableProps}
        />
        <CustomPagination
          total={leadForms?.totalElements}
          current={page}
          pageSize={PAGE_LENGTH}
          onChange={onPageChange}
          noBorder={true}
        />
      </div>
      <div className="text-color-030334 heading12px mt10">
        *Note: Deactivating a form will disable its link to process, making it
        inaccessible.
      </div>
      <Modal
        title={<></>}
        open={!!showPreview}
        centered
        className="form-preview"
        width={650}
        footer={null}
        loading={previewLoading}
        onOk={handleCancel}
        onCancel={handleCancel}
      >
        <div className="overflow-y-scroll">
          <Preview generatedHtml={generatedHtml} />
        </div>
      </Modal>
    </div>
  );
};

export default FormsList;
